import { Box, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';

const RED = '#EB0A1E';
const BORDER = '#DDE1E7';
const INK = '#111827';
const EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';

const shouldForwardCompact = (prop: PropertyKey) => prop !== 'compact';

interface CompactProps {
  compact?: boolean;
}

export const DocumentPage = styled(Box)(() => `
  min-height: calc(100vh - 64px);
  padding: 24px 28px 56px;
  background: #ffffff;
  color: ${INK};

  @media (max-width: 720px) {
    padding: 18px 16px 40px;
  }
`);

export const BackButton = styled('button')(() => `
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  margin: 0 0 22px;
  border: 0;
  background: transparent;
  color: ${RED};
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
`);

export const DocumentLayout = styled(Box)(() => `
  display: grid;
  grid-template-columns: minmax(0, 1fr) 292px;
  gap: 48px;
  max-width: 1420px;
  margin: 0 auto;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
    gap: 28px;
  }
`);

export const Article = styled('main')(() => `
  min-width: 0;
`);

export const HeaderRow = styled(Box, { shouldForwardProp: shouldForwardCompact })<CompactProps>(({ theme, compact }) => `
  position: sticky;
  top: ${theme.header.height};
  z-index: 5;
  display: flex;
  align-items: ${compact ? 'center' : 'flex-start'};
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 18px;
  padding: ${compact ? '8px 0' : '14px 0'};
  background: #ffffff;
  border-bottom: 1px solid ${compact ? BORDER : 'transparent'};
  box-shadow: ${compact ? '0 4px 14px rgba(0, 0, 0, 0.06)' : 'none'};
  transition: padding 0.3s ${EASE}, border-color 0.3s ${EASE}, box-shadow 0.3s ${EASE};

  @media (max-width: 720px) {
    flex-direction: column;
  }
`);

export const TitleGroup = styled(Box, { shouldForwardProp: shouldForwardCompact })<CompactProps>(({ compact }) => `
  display: flex;
  align-items: center;
  gap: ${compact ? '12px' : '18px'};
  min-width: 0;
  transition: gap 0.3s ${EASE};
`);

export const DocumentIcon = styled(Box, { shouldForwardProp: shouldForwardCompact })<CompactProps>(({ compact }) => `
  display: grid;
  place-items: center;
  flex: 0 0 ${compact ? '40px' : '64px'};
  width: ${compact ? '40px' : '64px'};
  height: ${compact ? '40px' : '64px'};
  border-radius: 8px;
  background: #FFE9EB;
  color: ${RED};
  transition: flex-basis 0.3s ${EASE}, width 0.3s ${EASE}, height 0.3s ${EASE};

  & svg {
    font-size: ${compact ? '24px' : '38px'};
    transition: font-size 0.3s ${EASE};
  }
`);

export const DocumentTitle = styled('h1', { shouldForwardProp: shouldForwardCompact })<CompactProps>(({ compact }) => `
  margin: 0 0 ${compact ? '0' : '10px'};
  color: #101828;
  font-family: 'Toyota', sans-serif;
  font-size: ${compact ? '18px' : '27px'};
  line-height: 1.2;
  font-weight: 700;
  letter-spacing: 0;
  transition: font-size 0.3s ${EASE}, margin 0.3s ${EASE};
`);

export const ChipRow = styled(Box, { shouldForwardProp: shouldForwardCompact })<CompactProps>(({ compact }) => `
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  overflow: hidden;
  max-height: ${compact ? '0px' : '200px'};
  opacity: ${compact ? '0' : '1'};
  transition: max-height 0.3s ${EASE}, opacity 0.3s ${EASE};
`);

export const TopicChip = styled('span')(() => `
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 2px 9px;
  border: 0;
  border-radius: 4px;
  background: #EEF2FF;
  color: #22346D;
  font: inherit;
  font-size: 11px;
  font-weight: 700;

  &:is(button) {
    cursor: pointer;
  }

  &:is(button):hover {
    background: #DCE5FF;
  }
`);

export const ActionGroup = styled(Box)(() => `
  display: flex;
  gap: 8px;
  flex: 0 0 auto;
`);

export const ActionButton = styled('button')(() => `
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 38px;
  padding: 0 13px;
  border: 1px solid ${BORDER};
  border-radius: 5px;
  background: #ffffff;
  color: #344054;
  font: inherit;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    border-color: #AEB5C0;
    background: #FAFAFB;
  }
`);

export const MoreButton = styled(IconButton)(() => `
  width: 38px;
  height: 38px;
  border: 1px solid ${BORDER};
  border-radius: 5px;
  color: #344054;
`);

export const Summary = styled('p')(() => `
  margin: 0 0 14px;
  color: #344054;
  font-size: 14px;
  line-height: 1.6;
`);

export const InlineMeta = styled(Box)(() => `
  display: flex;
  flex-wrap: wrap;
  gap: 0;
  padding-bottom: 20px;
  margin-bottom: 24px;
  border-bottom: 1px solid ${BORDER};
  color: #475467;
  font-size: 12px;
`);

export const InlineMetaItem = styled('span')(() => `
  display: inline-flex;
  gap: 4px;
  align-items: center;

  &:not(:last-child)::after {
    content: '';
    width: 1px;
    height: 13px;
    margin: 0 13px;
    background: #D0D5DD;
  }
`);

export const SideRail = styled('aside')(({ theme }) => `
  display: flex;
  flex-direction: column;
  gap: 20px;
  position: sticky;
  top: calc(${theme.header.height} + 24px);
  align-self: flex-start;
  height: calc(100vh - ${theme.header.height} - 100px);
  overflow: hidden;

  @media (max-width: 980px) {
    position: static;
    height: auto;
    overflow: visible;
  }
`);

export const RailPanel = styled(Box)(() => `
  border: 1px solid ${BORDER};
  border-radius: 6px;
  background: #ffffff;
  overflow: hidden;
  flex-shrink: 0;
`);

export const RailPanelBody = styled(Box)(() => `
  padding: 18px;
`);

export const RailTitle = styled('h2')(() => `
  margin: 0 0 18px;
  color: #101828;
  font-size: 13px;
  line-height: 1.3;
  font-weight: 700;
  letter-spacing: 0;
`);

export const MetadataList = styled('dl')(() => `
  display: grid;
  grid-template-columns: 82px minmax(0, 1fr);
  gap: 15px 10px;
  margin: 0;
  color: #344054;
  font-size: 11px;
  width: 100%;

  dt { margin: 0; color: #475467; }
  dd { margin: 0; color: #101828; }
`);

export const PopularHeader = styled(Box)(() => `
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 18px 8px;
  color: #101828;

  svg { color: ${RED}; }
`);

export const PopularTitle = styled('h2')(() => `
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0;
`);

export const PopularItem = styled('button')(() => `
  display: grid;
  grid-template-columns: 30px minmax(0, 1fr);
  gap: 9px;
  width: 100%;
  padding: 11px 18px;
  border: 0;
  border-top: 1px solid #EEF0F3;
  background: #ffffff;
  text-align: left;
  cursor: pointer;

  &:hover { background: #FAFAFB; }
`);

export const PopularIcon = styled('span')(() => `
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: 5px;
  background: #FFF0F1;
  color: ${RED};
`);

export const PopularItemTitle = styled('strong')(() => `
  display: block;
  color: #101828;
  font-size: 11px;
  line-height: 1.35;
`);

export const PopularItemDate = styled('span')(() => `
  display: block;
  margin-top: 3px;
  color: #667085;
  font-size: 10px;
`);

export const HelpPanel = styled(RailPanel)(() => `
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr);
  gap: 12px;
  padding: 18px;
`);

export const HelpIcon = styled('span')(() => `
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  color: #101828;
`);

export const HelpTitle = styled('h2')(() => `
  margin: 0 0 7px;
  color: #101828;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0;
`);

export const HelpText = styled('p')(() => `
  margin: 0 0 12px;
  color: #475467;
  font-size: 10px;
  line-height: 1.55;
`);

export const HelpLink = styled('a')(() => `
  color: ${RED};
  font-size: 11px;
  font-weight: 700;
  text-decoration: none;
`);
